/* =========================================================================
   FC — Variant-Aware Media Gallery (desktop only)
   ---------------------------------------------------------------------------
   Behaviour (agreed with client — updated 2026-09-03):
   - Every variant (including the first / "4-speler" one, and the variant
     selected on page load) is filtered the same way. For the selected
     variant the gallery shows:
                                   (a) media whose alt text contains the
                                       variant's exact SKU as a whole word, AND
                                   (b) "shared" media: any media whose alt text
                                       contains NONE of this product's variant
                                       SKUs (lifestyle / detail / infographic
                                       shots, video, etc.).
                                 Everything else (media tagged with a DIFFERENT
                                 variant's SKU) is hidden.
                                 When COLOR_PHOTO_FIRST is on, the SKU-matched
                                 media are pulled to the FRONT of the gallery
                                 (so the selected colour shows first), followed
                                 by the shared media. When VIDEO_LAST is on, the
                                 product video / 3D model is pushed BELOW every
                                 photo and kept visible there. Photos are then
                                 capped at MAX_VISIBLE so the gallery stays tidy
                                 (the video does not count toward the cap).
   - No matching media         -> fall back to the full (unfiltered) gallery.

   - The fullscreen PhotoSwipe lightbox (opened by clicking the main image)
     is rebuilt to match: it lists exactly the media the filtered grid shows,
     in the same order (selected colour's photos first, then the shared ones,
     then the video), and a click opens on the photo that was clicked. See
     rebuildLightbox() / syncLightbox() below. Without this the lightbox keeps
     the theme's full 25-image list and opens on the wrong frame, because the
     theme builds the lightbox once from every cell and its click handler
     treats the cell's data-index as a raw slide position.

   Match rule (changed 2026-08-27):
   - The alt text is split into whitespace-separated tokens; a token must
     EQUAL the SKU (case-insensitive) to count as a match. Previously the rule
     was a raw substring check, which made SKU "UG570" also match media tagged
     "UG570-NR" / "UG570-BW". Whole-word matching fixes that collision.

   Notes:
   - Desktop only. The mobile gallery is a separate <media-gallery> and is left
     untouched, matching the desktop-only 5-item requirement.
   - Video / model media is filtered by the same rule as images
     (see FILTER_NON_IMAGE_MEDIA). A video with an empty alt text counts as
     "shared" and is therefore shown on every variant.
   - If the theme's built-in "variant group images" feature is enabled we bail
     out entirely so the two systems never fight over the gallery.
   - The default view's 5-item cap is a positional CSS rule (:nth-child(n+6)
     on [data-minimal-gallery="true"]). It cannot coexist with content-based
     hiding, so on a filtered variant we switch the attribute off and instead
     apply our own count cap (MAX_VISIBLE) to the media that survived the
     variant filter, driving visibility with the .fc-media-hidden class.

   Developer: Alfie / Jon
   Updated:   2026-09-03
   ========================================================================= */
(function () {
  'use strict';

  /* When true, videos / models are filtered by the same rule as images:
     on a non-default variant a video is hidden unless its alt text matches
     the variant's SKU or the video counts as "shared" (no SKU in the alt).
     Flip to false to keep all videos/models visible regardless of variant. */
  var FILTER_NON_IMAGE_MEDIA = true;

  /* Maximum number of media items shown on a filtered (non-default) variant,
     counted AFTER the variant filter. Keeps the gallery tidy even when a
     colour has many photos of its own plus all the shared ones. The media
     that make the cut are simply the first MAX_VISIBLE in the product's
     media order (Shopify admin -> product -> Media -> drag to reorder). */
  var MAX_VISIBLE = 6;

  /* When true, a filtered variant shows its own SKU-matched media FIRST, then
     the shared media, regardless of their position in the product's media
     list. This is what makes "Orange/Yellow" open on the orange/yellow set
     photos instead of a generic lifestyle shot. Set to false to keep the
     plain media order (shared shots can then lead). */
  var COLOR_PHOTO_FIRST = true;

  /* When true, video / 3D-model media is pushed BELOW every photo on a
     filtered variant, and is exempt from the MAX_VISIBLE cap so the product
     film always stays visible at the very bottom of the gallery (client
     request 2026-08-27: "vooral het filmpje moet onderaan staan op de pdp").
     The ordering within the gallery then reads:
       1. the selected colour's own photos
       2. the shared photos (lifestyle / detail / infographic)
       3. the video, last.
     On the desktop grid layout this also means overriding the inline CSS
     `order` the theme puts on the cells for its "featured" arrangement —
     without that the video keeps floating up to the third slot. */
  var VIDEO_LAST = true;

  /* Every SKU on the current product (filled in by init() from the embedded
     #productVariants JSON). Used to decide whether a media item is "shared". */
  var PRODUCT_SKUS = [];

  /* The needle to look for in each alt: the selected variant's exact SKU. */
  function skuNeedle(variant) {
    return variant && variant.sku ? String(variant.sku).trim() : '';
  }

  function altOf(cell) {
    var node = cell.querySelector('[data-media-alt]');
    if (node && node.dataset.mediaAlt) return node.dataset.mediaAlt;
    var img = cell.querySelector('img');
    return img ? img.getAttribute('alt') || '' : '';
  }

  function mediaTypeOf(cell) {
    if (cell.dataset && cell.dataset.mediaType) return cell.dataset.mediaType;
    var m = cell.className.match(/media-type-([\w-]+)/);
    return m ? m[1] : '';
  }

  /* Split an alt string into comparable tokens: whitespace-separated, with
     leading/trailing punctuation stripped but "-" and "/" kept inside a token
     (SKUs contain them, e.g. "UG-Oranje/geel-Hard"). */
  function altTokens(cell) {
    return altOf(cell)
      .split(/\s+/)
      .map(function (t) {
        return t.replace(/^[^\w/-]+|[^\w/-]+$/g, '');
      })
      .filter(Boolean);
  }

  function tokenEq(a, b) {
    return String(a).toLowerCase() === String(b).toLowerCase();
  }

  /* True when the alt text contains `sku` as a whole token. */
  function altHasSku(cell, sku) {
    if (!sku) return false;
    var toks = altTokens(cell);
    for (var i = 0; i < toks.length; i++) {
      if (tokenEq(toks[i], sku)) return true;
    }
    return false;
  }

  /* True when the alt text contains NONE of this product's variant SKUs —
     i.e. a generic image/video that should show for every variant. */
  function isSharedMedia(cell) {
    var toks = altTokens(cell);
    for (var i = 0; i < toks.length; i++) {
      for (var j = 0; j < PRODUCT_SKUS.length; j++) {
        if (PRODUCT_SKUS[j] && tokenEq(toks[i], PRODUCT_SKUS[j])) return false;
      }
    }
    return true;
  }

  /* ----------------------------------------------------------------------- */

  function setupGallery(gallery) {
    if (gallery.__fcGallery) return gallery.__fcGallery;

    /* Original cap state: "true" when the merchant enabled the minimal
       gallery, otherwise null (no cap — the default is all images). */
    var originalMinimal = gallery.getAttribute('data-minimal-gallery');

    function mainCells() {
      return gallery.querySelectorAll('.m-product-media--item');
    }
    function thumbCells() {
      return gallery.querySelectorAll(
        '.m-product-media--slider__thumbnails .swiper-slide'
      );
    }

    /* Snapshot the media in their original (admin) order so we can both
       reorder from a known baseline and put everything back on showDefault(). */
    var origMainCells = Array.prototype.slice.call(mainCells());
    var origThumbCells = Array.prototype.slice.call(thumbCells());

    /* The desktop grid layout builds a "featured" arrangement by putting an
       inline `order` on the media cells (roughly: first two images order 0,
       the video order 1, the rest order 2). That inline `order` beats DOM
       position, so simply re-appending the video last is not enough — it
       still floats up to slot 3 visually. On a filtered variant we therefore
       take `order` over ourselves (see applyList) and restore these originals
       in restoreOrder(). */
    var origMainOrder = origMainCells.map(function (c) {
      return c.style.order || '';
    });

    function clearHidden() {
      gallery
        .querySelectorAll('.fc-media-hidden')
        .forEach(function (el) {
          el.classList.remove('fc-media-hidden');
        });
    }

    /* Re-append every cell in its original order (appendChild moves nodes). */
    function restoreOrder() {
      [origMainCells, origThumbCells].forEach(function (arr) {
        if (!arr.length) return;
        var parent = arr[0].parentNode;
        if (!parent) return;
        arr.forEach(function (cell) {
          parent.appendChild(cell);
        });
      });
      /* Give the theme its own `order` values back. */
      origMainCells.forEach(function (cell, i) {
        cell.style.order = origMainOrder[i];
      });
    }

    /* Re-sync any Swiper instance (slider layouts) and the grid "5 / 15"
       counter, which recalculates on window resize. Grid layout has no
       slider, so the slider calls are simply skipped. */
    function refresh() {
      if (gallery.slider && typeof gallery.slider.update === 'function') {
        try {
          gallery.slider.update();
          gallery.slider.slideTo(0, 0);
        } catch (e) {}
      }
      if (gallery.navSlider && typeof gallery.navSlider.update === 'function') {
        try {
          gallery.navSlider.update();
        } catch (e) {}
      }
      window.dispatchEvent(new Event('resize'));
    }

    /* -------------------------------------------------------------------
       Fullscreen PhotoSwipe lightbox.

       The theme (media-gallery.handlePhotoswipe / initPhotoswipe) builds the
       lightbox ONCE, during its own init(), from every .m-product-media--item
       cell — hidden ones included — and its open-on-click handler passes the
       clicked cell's data-index straight into lightbox.loadAndOpen() as a
       raw slide position. After we filter and reorder the grid, that leaves
       the lightbox showing all 25 photos and opening on the wrong frame.

       rebuildLightbox() re-creates gallery.lightbox via the theme's own
       handlePhotoswipe(dataSource) entry point (it accepts an override array
       as its first argument) with just the cells we want, in grid order, and
       wraps loadAndOpen() so the clicked photo still maps to the right slide.
       Passing null restores the theme's full lightbox.

       Each dataSource entry keeps `id` = the cell's real data-index, so the
       theme's own thumbEl / placeholderSrc filters (which look the cell up by
       [data-index="<id>"]) keep resolving.
       ------------------------------------------------------------------- */
    function lightboxEntry(cell) {
      if (mediaTypeOf(cell) === 'image') {
        var d = cell.querySelector('.m-product-media').dataset;
        return {
          src: d.mediaSrc,
          width: parseInt(d.mediaWidth, 10),
          height: parseInt(d.mediaHeight, 10),
          alt: d.mediaAlt,
          id: cell.dataset.index,
        };
      }
      return {
        html:
          '<div class="pswp__' +
          cell.dataset.mediaType +
          '">' +
          cell.innerHTML +
          '</div>',
        type: cell.dataset.mediaType,
        id: cell.dataset.index,
      };
    }

    function rebuildLightbox(orderedCells) {
      if (!gallery.enableImageZoom) return;
      if (typeof gallery.handlePhotoswipe !== 'function') return;
      if (!window.MinimogLibs || !window.MinimogLibs.PhotoSwipeLightbox) return;

      try {
        if (gallery.lightbox && typeof gallery.lightbox.destroy === 'function') {
          gallery.lightbox.destroy();
        }
      } catch (e) {}

      if (!orderedCells) {
        /* Back to the theme's full lightbox (every cell, admin order). */
        gallery.handlePhotoswipe();
        try {
          gallery.lightbox.init();
        } catch (e) {}
        return;
      }

      var ds = orderedCells.map(lightboxEntry);
      gallery.handlePhotoswipe(ds);
      try {
        gallery.lightbox.init();
      } catch (e) {}

      /* The theme's click handler calls loadAndOpen(clickedCell.dataset.index);
         translate that original media index into the slide's position inside
         the filtered dataSource. */
      var open = gallery.lightbox.loadAndOpen.bind(gallery.lightbox);
      gallery.lightbox.loadAndOpen = function (idx) {
        var pos = 0;
        for (var i = 0; i < ds.length; i++) {
          if (String(ds[i].id) === String(idx)) {
            pos = i;
            break;
          }
        }
        return open(pos);
      };
    }

    /* Read the current (already-filtered) grid and bring the lightbox in
       line with it. No-op until the theme has finished building its own
       lightbox — the deferred call in init() picks it up after that. */
    function syncLightbox() {
      if (!gallery.enableImageZoom) return;
      if (gallery.hasAttribute('data-media-loading')) return;
      if (!gallery.querySelector('.m-product-media--list')) return; /* grid only */

      var cells = Array.prototype.slice.call(
        gallery.querySelectorAll(
          '.m-product-media--item:not(.swiper-slide-duplicate)'
        )
      );
      if (!cells.length) return;

      var anyHidden = cells.some(function (c) {
        return c.classList.contains('fc-media-hidden');
      });
      if (!anyHidden) {
        rebuildLightbox(null);
        return;
      }
      var visible = cells.filter(function (c) {
        return !c.classList.contains('fc-media-hidden');
      });
      rebuildLightbox(visible);
    }

    function showDefault() {
      if (originalMinimal === null) {
        gallery.removeAttribute('data-minimal-gallery');
      } else {
        gallery.setAttribute('data-minimal-gallery', originalMinimal);
      }
      restoreOrder();
      clearHidden();
      refresh();
      syncLightbox();
    }

    function filterByVariant(needle) {
      /* No SKU on the variant -> nothing to match on; keep the default view. */
      if (!needle) {
        showDefault();
        return;
      }

      /* A cell is kept when it is tagged with the selected SKU, or when it is
         shared media (no variant SKU in its alt at all). Non-image media
         follows the same rule unless FILTER_NON_IMAGE_MEDIA is off. */
      function keepCell(cell) {
        var isImage = mediaTypeOf(cell) === 'image';
        if (!isImage && !FILTER_NON_IMAGE_MEDIA) return true;
        return altHasSku(cell, needle) || isSharedMedia(cell);
      }

      /* Count keepers first so we can honour the zero-match fallback before
         mutating anything. */
      var matches = 0;
      origMainCells.forEach(function (cell) {
        if (keepCell(cell)) matches++;
      });

      if (matches === 0) {
        showDefault();
        return;
      }

      /* Disable the positional default-view cap; we drive visibility by
         content here, then re-cap the survivors at MAX_VISIBLE. */
      gallery.setAttribute('data-minimal-gallery', 'false');

      /* For one list (main cells or thumbnails), working from the original
         order:
           1. keep only the cells that pass the variant filter
           2. re-order by orderRank (colour photos, shared photos, video)
           3. re-attach in that order, hiding photos past MAX_VISIBLE
           4. park the filtered-out cells at the end, hidden */
      /* Sort key: 0 = the selected colour's own photos, 1 = shared photos,
         2 = video / 3D model (only when VIDEO_LAST). A lower number wins, and
         Array.prototype.sort is stable, so media keep their admin order within
         each group. The same number is also written to the cell's inline CSS
         `order` (grid layout) so the theme's featured-layout `order` can't
         float the video back up above the photos. */
      function orderRank(cell) {
        if (COLOR_PHOTO_FIRST && altHasSku(cell, needle)) return 0;
        if (VIDEO_LAST && mediaTypeOf(cell) !== 'image') return 2;
        return 1;
      }

      function applyList(orig) {
        if (!orig.length) return;
        var parent = orig[0].parentNode;
        if (!parent) return;

        /* Only the desktop grid list uses CSS `order`; the slider/thumbnail
           wrapper must not be touched (Swiper positions by transform). */
        var isGrid =
          parent.classList &&
          parent.classList.contains('m-product-media--list');

        var kept = orig.filter(keepCell);
        if (COLOR_PHOTO_FIRST || VIDEO_LAST) {
          kept.sort(function (a, b) {
            return orderRank(a) - orderRank(b);
          });
        }

        /* The MAX_VISIBLE cap counts photos only. Video / 3D-model media is
           never counted and never hidden by the cap (VIDEO_LAST) so the
           product film always stays visible at the bottom of the gallery. */
        var shownPhotos = 0;
        kept.forEach(function (cell) {
          var isImage = mediaTypeOf(cell) === 'image';
          var visible;
          if (isImage || !VIDEO_LAST) {
            visible = shownPhotos < MAX_VISIBLE;
            if (visible) shownPhotos++;
          } else {
            visible = true;
          }
          cell.classList.toggle('fc-media-hidden', !visible);
          if (isGrid) cell.style.order = String(orderRank(cell));
          parent.appendChild(cell);
        });

        orig.forEach(function (cell) {
          if (kept.indexOf(cell) === -1) {
            cell.classList.add('fc-media-hidden');
            if (isGrid) cell.style.order = '99';
            parent.appendChild(cell);
          }
        });
      }

      applyList(origMainCells);
      applyList(origThumbCells);
      refresh();
      syncLightbox();
    }

    gallery.__fcGallery = {
      showDefault: showDefault,
      syncLightbox: syncLightbox,
      filter: function (variant) {
        filterByVariant(skuNeedle(variant));
      },
    };
    return gallery.__fcGallery;
  }

  /* -----------------------------------------------------------------------
     Selecting a variant must never scroll the page.

     In "grid-column" mode (the desktop product-template layout, where the
     media list has no slider) the theme's media-gallery reacts to a variant
     change by calling scrollIntoView({behavior:'smooth'}) on the media cell
     of the new variant's featured image. That yanks the page down to that
     cell — and because this script hides non-matching media at the same
     moment, the collapsing column makes the smooth scroll overshoot, landing
     at the bottom of the page.

     The filtering above already puts the matching media at the top of the
     gallery, so the scroll has nothing left to do: neutralise it. Only the
     class's own method is replaced; Element.prototype.scrollIntoView is
     untouched for every other element on the page.
     ----------------------------------------------------------------------- */
  function preventGalleryPageJump() {
    if (!window.customElements || !customElements.whenDefined) return;
    customElements.whenDefined('media-gallery').then(function () {
      var Gallery = customElements.get('media-gallery');
      if (!Gallery || Gallery.prototype.__fcNoScroll) return;
      Gallery.prototype.scrollIntoView = function () {};
      Gallery.prototype.__fcNoScroll = true;
    });
  }

  /* Run `cb` once the theme's media-gallery has finished its own init() —
     i.e. it has dropped [data-media-loading] and (when image zoom is on)
     built gallery.lightbox. Falls back to firing anyway after 5s. */
  function whenGalleryReady(gallery, cb) {
    var done = false;
    function ready() {
      if (done) return false;
      if (gallery.hasAttribute('data-media-loading')) return false;
      if (gallery.enableImageZoom && !gallery.lightbox) return false;
      return true;
    }
    function fire() {
      if (done) return;
      done = true;
      clearInterval(iv);
      if (obs) obs.disconnect();
      cb();
    }
    if (ready()) return cb();
    var obs = null;
    if (window.MutationObserver) {
      obs = new MutationObserver(function () {
        if (ready()) fire();
      });
      obs.observe(gallery, { attributes: true });
    }
    var iv = setInterval(function () {
      if (ready()) fire();
    }, 150);
    setTimeout(fire, 5000);
  }

  /* ----------------------------------------------------------------------- */

  function init() {
    preventGalleryPageJump();

    var main =
      document.querySelector('.m-main-product[data-product-id]') ||
      document.querySelector('[data-section-type="product-page"][data-product-id]');
    var gallery = document.querySelector('.m-product-media--desktop media-gallery');
    if (!gallery || !main) return;

    var productId = main.dataset.productId;

    /* Respect the built-in variant-group-images feature — never both. */
    if (gallery.getAttribute('data-enable-variant-group-images') === 'true') return;

    var controller = setupGallery(gallery);

    /* Variant lookup + the id selected on page load. */
    var variants = [];
    try {
      var node = document.querySelector('#productVariants[type="application/json"]');
      if (node) variants = JSON.parse(node.textContent) || [];
    } catch (e) {}

    var byId = {};
    variants.forEach(function (v) {
      byId[Number(v.id)] = v;
    });

    /* Collect every SKU on the product so isSharedMedia() can tell a generic
       image from one that belongs to another variant. */
    PRODUCT_SKUS = variants
      .map(function (v) {
        return v && v.sku ? String(v.sku).trim() : '';
      })
      .filter(Boolean);

    var idInput = document.querySelector(
      '.m-product-info--wrapper [name="id"], .m-main-product form [name="id"]'
    );
    var initialVariantId = idInput ? Number(idInput.value) : null;

    /* Dedupe: the theme event and the id-input change can both fire for the
       same selection; only act once per distinct variant id. */
    var lastId = null;

    function handle(variant) {
      if (!variant || variant.id == null) return;
      var id = Number(variant.id);
      if (id === lastId) return;
      lastId = id;

      /* Every variant — including the first / "default" one — runs through the
         same filter, so the 4-player set also gets colour-first ordering, the
         MAX_VISIBLE cap and video-last (client request 2026-08-27: the
         4-speler view was "heel onrustig" with every photo shown). A variant
         with no SKU, or one whose SKU matches no media, falls back to the full
         gallery inside filterByVariant().

         Match on SKU; use whichever object actually carries one. Both the
         theme event (/product.js) and the embedded #productVariants JSON
         include `sku`, but guard in case the event payload lacks it. */
      var source = variant.sku ? variant : byId[id] || variant;
      controller.filter(source);
    }

    if (window.MinimogEvents && typeof window.MinimogEvents.subscribe === 'function') {
      window.MinimogEvents.subscribe(productId + '__VARIANT_CHANGE', handle);
    }
    if (idInput) {
      idInput.addEventListener('change', function () {
        var v = byId[Number(idInput.value)];
        if (v) handle(v);
      });
    }

    /* Filter for the variant that is already selected on page load, so the
       gallery is tidy immediately instead of only after the first change. */
    var initialVariant = initialVariantId ? byId[initialVariantId] : null;
    if (initialVariant) handle(initialVariant);

    /* The inline grid is filtered synchronously above, but the theme builds
       its PhotoSwipe lightbox later, at the end of its own async init(). Once
       that is done, bring the lightbox in line with whatever variant is now
       showing. */
    whenGalleryReady(gallery, function () {
      controller.syncLightbox();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
